import {
  put, call, takeLatest, cancelled, race, delay, select
} from 'redux-saga/effects';
import { get } from 'lodash';
import axios from 'axios';
import { Notification } from '../../components/ui/custom-notification';
import {
  FETCH_WELD_DATA,
  FETCH_WELD_PASSES,
  FETCH_ZONE_VIEW,
  FETCH_TILT_VIEW,
  FETCH_PASS_VIEW_DETAILS,
  FETCH_WELD_SEGMENT_VIEW,
  FETCH_WELD_SEGMENT_DETAIL,
  FETCH_TILT_VIEW_DETAILS,
  FETCH_ZONE_VIEW_DETAILS,
  FETCH_PROJECTS,
  FETCH_SLOGS_REQUEST,
  GENERATE_REPORT_REQUEST,
  FETCH_LIMITS_DATA,
  UPDATE_STATUS_CONFIGURATION,
  FETCH_MANUAL_STATUS_CONFIGURATION,
  UPDATE_MANUAL_STATUS_CONFIGURATION,
  ASSIGN_PIPE_TO_WELD,
  ASSIGN_JOB_NUMBER_TO_PIPE,
  UPDATE_WELD_NUMBER,
  RENAME_JOB_NUMBER,
  MERGE_PASSES
} from './types';
import {
  setWeldData,
  setWeldDataFailure,
  setWeldPasses,
  setWeldPassesFailure,
  setZoneView,
  setZoneViewFailure,
  setTiltView,
  setTiltViewFailure,
  setPassViewDetails,
  setPassViewDetailsFailure,
  setWeldSegmentView,
  setWeldSegmentViewFailure,
  setWeldSegmentDetail,
  setWeldSegmentDetailFailure,
  setZoneViewDetails,
  setZoneViewDetailsFailure,
  setProjects,
  setProjectsFailure,
  setSlogsData,
  setSlogsDataFailure,
  setReportGenerationSuccess,
  setReportGenerationFailure,
  setLimitsData,
  setLimitsDataFailure,
  updateStatusConfigurationSuccess,
  updateStatusConfigurationFailure,
  setManualStatusConfiguration,
  setManualStatusConfigurationFailure,
  updateManualStatusConfigurationSuccess,
  updateManualStatusConfigurationFailure,
  assignPipeToWeldSuccess,
  assignPipeToWeldFailure,
  assignJobNumberToPipeSuccess,
  assignJobNumberToPipeFailure,
  updateWeldNumberSuccess,
  updateWeldNumberFailure,
  renameJobNumberSuccess,
  renameJobNumberFailure,
  mergePassesSuccess,
  mergePassesFailure,
  restoreMergedPassesSuccess,
  restoreMergedPassesFailure,
  fetchWeldData,
  fetchWeldPasses
} from './actions';
import axiosConfig, { axiosConfigV2 } from './axiosConfig';
import {
  mergeWeldDataResults,
  normalizePipeIds,
  tagWeldRowsWithPipe,
} from './weldStatusMerge';
import { normalizeWeldStationNumber } from './utils/normalizeWeldStationNumber';
import {
  resolvePassBandDiameter,
  resolvePassBugNumber,
  resolvePassPipeDiameter,
  resolvePassWeldLength,
} from '../../utils/passMetadataUtils';

function transformWeldRecord(weld, statusCalculationMethod) {
  let status = 'Completed';
  if (statusCalculationMethod) {
    if (statusCalculationMethod === 'Instantaneous') {
      status = weld.statusByInstantaneous ? 'Completed' : 'Rejected';
    } else if (statusCalculationMethod === 'AvgByZone') {
      status = weld.statusByAvgZone ? 'Completed' : 'Rejected';
    } else if (statusCalculationMethod === 'AvgByPass') {
      status = weld.statusByAvgPass ? 'Completed' : 'Rejected';
    } else if (statusCalculationMethod === 'AvgByTilt') {
      status = weld.statusByAvgTilt ? 'Completed' : 'Rejected';
    }
  } else if (
    weld.statusByInstantaneous === 'NULL' ||
    weld.statusByAvgZone === 'NULL' ||
    weld.statusByAvgPass === 'NULL' ||
    weld.statusByAvgTilt === 'NULL'
  ) {
    status = 'Rejected';
  } else if (
    weld.statusByInstantaneous === 'FALSE' ||
    weld.statusByAvgZone === 'FALSE' ||
    weld.statusByAvgPass === 'FALSE' ||
    weld.statusByAvgTilt === 'FALSE'
  ) {
    status = 'In Progress';
  }

  return {
    id: weld.id,
    weldNumber: weld.weldNumber,
    wpsNumber: weld.wpsNumber,
    weldIdentifier: `WLD-${weld.projectId}-${weld.weldNumber}`,
    status,
    weldTime: `${weld.weld_time} mins`,
    startTime: weld.start_time,
    endTime: weld.end_time,
    projectId: weld.projectId,
    projectName: weld.projectName,
    job_number: weld.job_number,
    statusByInstantaneous: weld.statusByInstantaneous,
    statusByAvgZone: weld.statusByAvgZone,
    statusByAvgPass: weld.statusByAvgPass,
    statusByAvgTilt: weld.statusByAvgTilt,
    gps: weld.gps,
    minDistance: weld.min_distance,
    numOfGpsPoints: weld.num_of_gps_points,
    pipe_diameter: weld.pipe_diameter,
    wall_thickness: weld.wall_thickness,
    stationNumber: normalizeWeldStationNumber(weld),
    isFileUpload: weld.is_fileUpload === true || weld.isFileUpload === true,
  };
}

async function fetchWeldDataForPipe(projectId, projectName, pipe_id) {
  let url = `/weld-status?project_id=${projectId}`;
  if (projectName) {
    url += `&project_name=${encodeURIComponent(projectName)}`;
  }
  // Do not send job_number — the API returns 500 when job_number is combined with
  // pipe_details_id. Production and Weld Analytics scope by pipe server-side and
  // filter by job client-side (see features/weld-analytics/utils/jobFilter.ts).
  if (pipe_id) {
    url += `&pipe_details_id=${encodeURIComponent(pipe_id)}`;
  }

  const res = await axiosConfig.get(url);

  if (res.status >= 200 && res.status < 300) {
    const statusCalculationMethod = get(res, ['data', 'data', 'statusCalculationMethod']);
    const weldData = get(res, ['data', 'data', 'data'], []);
    const transformedWeldData = weldData.map((weld) =>
      transformWeldRecord(weld, statusCalculationMethod),
    );

    return {
      weldData: transformedWeldData,
      statusCalculationMethod: statusCalculationMethod || 'Instantaneous',
    };
  }
  return { weldData: [], statusCalculationMethod: 'Instantaneous' };
}

// API functions
const api = {
  fetchWeldData: async (projectId, projectName, _job_number, pipe_id, pipeIds) => {
    const pipes = normalizePipeIds(pipeIds?.length ? pipeIds : pipe_id);
    if (pipes.length === 0) {
      // Project-wide fetch (no pipe filter) — keep legacy single call.
      return fetchWeldDataForPipe(projectId, projectName, null);
    }

    const FANOUT_CONCURRENCY = 3;
    const settled = new Array(pipes.length);
    let next = 0;
    const workers = Array.from(
      { length: Math.min(FANOUT_CONCURRENCY, pipes.length) },
      async () => {
        while (next < pipes.length) {
          const i = next;
          next += 1;
          const pid = pipes[i];
          try {
            const result = await fetchWeldDataForPipe(projectId, projectName, pid);
            settled[i] = {
              pipeId: pid,
              statusCalculationMethod: result.statusCalculationMethod,
              weldData: tagWeldRowsWithPipe(result.weldData, pid),
            };
          } catch (err) {
            settled[i] = {
              pipeId: pid,
              error: err?.message || 'Failed to load weld-status for pipe',
              weldData: [],
            };
          }
        }
      },
    );
    await Promise.all(workers);

    const merged = mergeWeldDataResults(settled);
    if (merged.weldData.length === 0 && merged.partialErrors.length === pipes.length) {
      throw new Error(
        merged.partialErrors.map((e) => `pipe ${e.pipeId}: ${e.error}`).join('; ')
          || 'Failed to load weld data for all selected pipes',
      );
    }
    return merged;
  },

  assignPipeToWeld: async (payload) => {
    try {
      const url = `/weld-status/update-by-weld-number`;
      const weldNumbers = payload.weld_number;
      const jobNumbers = payload.job_number;
      const pipeDetailsId = payload.pipe_details_id;
      const projectId = payload.project_id;

      const payloadWeldNumber = {
        weld_number: weldNumbers,
        project_id: projectId,
        pipe_details_id: pipeDetailsId
      };


      const res = await axiosConfig.put(url, payloadWeldNumber);

      //  const resJobNumber = await axiosConfig.put(`/weld-status/update-by-job-number`, payloadJobNumber);

      if (res.status >= 200 && res.status < 300) {
        Notification.success('Weld assigned to pipe successfully');
      }



    } catch (error) {
      console.error('❌ Saga - assignPipeToWeld error:', error);
      throw error;
    }
  },


  assignJobNumberToPipe: async (payload) => {
    try {
      const url = `/weld-status/update-by-job-number`;
      const jobNumbers = payload.job_number;
      const pipeDetailsId = payload.pipe_details_id;
      const projectId = payload.project_id;

      const payloadJobNumber = {
        job_number: jobNumbers,
        project_id: projectId,
        pipe_details_id: pipeDetailsId
      };


      const res = await axiosConfig.put(url, payloadJobNumber);

      if (res.status >= 200 && res.status < 300) {
        // Notification.success('Job number assigned to pipe successfully');
      }



    } catch (error) {
      console.error('❌ Saga - assignJobNumberToPipe error:', error);
      throw error;
    }
  },

  updateWelduidSEventTorch: async (payload) => {
    try {
      const url = `/log/update-welduid-s-event-torch`;
      const res = await axiosConfig.put(url, payload);

      if (res.status >= 200 && res.status < 300) {
        return res.data;
      }
      return null;
    } catch (error) {
      console.error('❌ Saga - updateWelduidSEventTorch error:', error);
      throw error;
    }
  },

  restoreWelduidSEventTorch: async (payload) => {
    try {
      const url = `/log/restore-welduid-s-event-torch`;
      const res = await axiosConfig.post(url, payload);

      if (res.status >= 200 && res.status < 300) {
        return res.data;
      }

      return null;
    } catch (error) {
      console.error('❌ Saga - restoreWelduidSEventTorch error:', error);
      throw error;
    }
  },



  fetchWeldPasses: async (projectId, projectName, weldNumber, jobNumber) => {
    try {
      let url = `/view/pass?project_id=${projectId}${projectName ? `&project_name=${encodeURIComponent(projectName)}` : ''}`;
      if (weldNumber) {
        url += `&weld_number=${encodeURIComponent(weldNumber)}`;
      }
      if (jobNumber) {
        url += `&job_number=${encodeURIComponent(jobNumber)}`;
      }

      const res = await axiosConfig.get(url);

      if (res.status >= 200 && res.status < 300) {
        const passPayload = get(res, ['data', 'data'], {});
        const weldPasses = Array.isArray(passPayload) ? passPayload : get(passPayload, 'data', []);
        const passDefaults = Array.isArray(passPayload) ? {} : passPayload;
        const conversionFactors = Array.isArray(passPayload)
          ? get(res, ['data', 'data', 'conversionFactors'], {})
          : (passPayload.conversionFactors ?? get(res, ['data', 'data', 'conversionFactors'], {}));

        // Debug: Log the first pass item to see the actual API response structure
        if (weldPasses.length > 0) {
          // First pass from API logged
        }

        // Transform API response to match frontend expected format
        const transformedWeldPasses = weldPasses.map(pass => {
          // Determine status based on the status fields
          let status = 'Completed';
          if (pass.pass_status_by_instantaneous === 'NULL' ||
            pass.pass_staus_by_zone === 'NULL' ||
            pass.pass_avg_status === 'NULL') {
            status = 'Rejected';
          } else if (pass.pass_status_by_instantaneous === 'FALSE' ||
            pass.pass_staus_by_zone === 'FALSE' ||
            pass.pass_avg_status === 'FALSE') {
            status = 'In Progress';
          }

          // Convert timestamps to readable format
          const startDate = new Date(pass.s_time * 1000);
          const endDate = new Date(pass.c_time * 1000);

          const formatDate = (date) => {
            return date.toLocaleDateString('en-US', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit'
            });
          };

          // Helper function to handle null values
          const handleNullValue = (value) => {
            return value !== null && value !== undefined ? value : null;
          };

          // Helper function to create parameter object with null handling
          const createParamObject = (low, avg, high, avgStatus, lowStatus, highStatus) => {
            const lowVal = handleNullValue(low);
            const avgVal = handleNullValue(avg);
            const highVal = handleNullValue(high);
            const avgStatusVal = handleNullValue(avgStatus);
            const lowStatusVal = handleNullValue(lowStatus);
            const highStatusVal = handleNullValue(highStatus);

            // Only return object if at least one value is not null
            if (lowVal !== null || avgVal !== null || highVal !== null) {
              return {
                min: lowVal,
                avg: avgVal,
                max: highVal,
                status: avgStatusVal, // Keep for backward compatibility
                avgStatus: avgStatusVal,
                lowStatus: lowStatusVal,
                highStatus: highStatusVal
              };
            }
            return null;
          };

          const bugNumber = resolvePassBugNumber(pass, passDefaults);
          const pipeDiameter = resolvePassPipeDiameter(pass, passDefaults);
          const bandDiameter = resolvePassBandDiameter(pass, passDefaults);
          const weldLength = resolvePassWeldLength(pass, passDefaults);

          return {
            id: pass.id,
            name: pass.pass_name || '-',
            status: status,
            is_merged: pass.is_merged,
            pass_status_by_instantaneous: pass.pass_status_by_instantaneous,
            pass_staus_by_zone: pass.pass_staus_by_zone,
            pass_avg_status: pass.pass_avg_status,
            avg_staus_by_instantaneous: pass.avg_staus_by_instantaneous,
            avg_tilt_status: pass.avg_tilt_status,
            avg_zone_status: pass.avg_zone_status,
            instantaneous_status: pass.instantaneous_status,
            welderId: pass.welder_id || '-',
            duration: pass.weld_time ? `${pass.weld_time} mins` : '-',
            startTime: pass.s_time ? formatDate(startDate) : '-',
            endTime: pass.c_time ? formatDate(endDate) : '-',
            torch: pass.torch || '-',
            bugType: pass.bug_type || '-',
            bugNumber: bugNumber || '-',
            bug_number: bugNumber,
            jobNumber: pass.job_number || pass.weld_uid || '-',
            weldingSystem: pass.welding_system || null,
            stationNumber: pass.station_number || '-',
            pipeName: pass.pipe_name || '-',
            zoneNames: pass.zone_names || [],
            pipeDiameter,
            pipe_diameter: pipeDiameter,
            bandDiameter,
            band_diameter: bandDiameter,
            weldLength,
            weld_length: weldLength,
            loggingDistance: handleNullValue(pass.logging_distance),
            wireDiameter: handleNullValue(pass.wire_diameter),
            welduid_s_event_torch: pass.welduid_s_event_torch || null,
            // Parameter data with min/avg/max values
            voltage: createParamObject(pass.volts_low, pass.volts_avg, pass.volts_high, pass.volts_avg_status, pass.volts_low_status, pass.volts_high_status),
            current: createParamObject(pass.current_low, pass.current_avg, pass.current_high, pass.current_avg_status, pass.current_low_status, pass.current_high_status),
            travelSpeed: createParamObject(pass.travel_speed_low, pass.travel_speed_avg, pass.travel_speed_high, pass.travel_speed_avg_status, pass.travel_speed_low_status, pass.travel_speed_high_status),
            heat: createParamObject(pass.heat_low, pass.heat_avg, pass.heat_high, pass.heat_avg_status, pass.heat_low_status, pass.heat_high_status),
            trueEnergy: createParamObject(pass.true_energy_low, pass.true_energy_avg, pass.true_energy_high, pass.true_energy_avg_status, pass.true_energy_low_status, pass.true_energy_high_status),
            oscillationWidth: (() => {
              const obj = createParamObject(pass.oscillation_width_low, pass.oscillation_width_avg, pass.oscillation_width_high, pass.oscillation_width_avg_status, pass.oscillation_width_low_status, pass.oscillation_width_high_status);

              return obj;
            })(),
            wireSpeed: createParamObject(pass.wire_speed_low, pass.wire_speed_avg, pass.wire_speed_high, pass.wire_speed_avg_status, pass.wire_speed_low_status, pass.wire_speed_high_status),
            // Additional data
            target: createParamObject(pass.target_low, pass.target_avg, pass.target_high),
            horizontalBias: createParamObject(pass.horizontal_bias_low, pass.horizontal_bias_avg, pass.horizontal_bias_high),
            frequency: createParamObject(pass.frequency_low, pass.frequency_avg, pass.frequency_high),
            totalWireConsumed: createParamObject(pass.total_wire_consumed_low, pass.total_wire_consumed_avg, pass.total_wire_consumed_high),
            arcImpedance: createParamObject(pass.arc_impedance_low, pass.arc_impedance_avg, pass.arc_impedance_high),
            gps: pass.gps || {},
            iwm: pass.iwm || {},
            conversionFactors: conversionFactors || {},
            autoincrement: pass.auto_increment || '-'
          };
        });

        // Debug: Log the first transformed pass to see what was created
        if (transformedWeldPasses.length > 0) {
          // First transformed pass logged
        }

        return transformedWeldPasses;
      }
      return [];
    } catch (error) {
      throw error;
    }
  },

  fetchZoneView: async (projectId, projectName, weldNumber, jobNumber) => {
    try {
      let url = `/view/zone?project_id=${projectId}${projectName ? `&project_name=${encodeURIComponent(projectName)}` : ''}`;
      if (weldNumber) {
        url += `&weld_number=${encodeURIComponent(weldNumber)}`;
      }
      if (jobNumber) {
        url += `&job_number=${encodeURIComponent(jobNumber)}`;
      }

      const res = await axiosConfig.get(url);

      if (res.status >= 200 && res.status < 300) {
        const zoneViewData = get(res, ['data', 'data', 'data'], []);

        // Transform API response to match frontend expected format
        const transformedZoneViewData = zoneViewData.map(zone => {
          // Determine status based on the status fields
          let status = 'Completed';
          if (zone.zone_status_by_instantaneuous === 'NULL' ||
            zone.avg_zone_status === 'NULL') {
            status = 'Rejected';
          } else if (zone.zone_status_by_instantaneuous === 'FALSE' ||
            zone.avg_zone_status === 'FALSE') {
            status = 'In Progress';
          }

          // Convert timestamps to readable format
          const startDate = new Date(zone.s_time * 1000);

          const formatDate = (date) => {
            const day = String(date.getDate()).padStart(2, '0');
            const month = date.toLocaleDateString('en-US', { month: 'short' });
            const year = date.getFullYear();
            const hours = date.getHours();
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            const ampm = hours >= 12 ? 'PM' : 'AM';
            const displayHours = hours % 12 || 12;
            return `${day}-${month}-${year}, ${displayHours}:${minutes}:${seconds} ${ampm}`;
          };

          // Helper function to handle null values
          const handleNullValue = (value) => {
            return value !== null && value !== undefined ? value : null;
          };

          // Helper function to create parameter object with null handling
          const createParamObject = (low, avg, high, avgStatus, lowStatus, highStatus) => {
            const lowVal = handleNullValue(low);
            const avgVal = handleNullValue(avg);
            const highVal = handleNullValue(high);
            const avgStatusVal = handleNullValue(avgStatus);
            const lowStatusVal = handleNullValue(lowStatus);
            const highStatusVal = handleNullValue(highStatus);

            // Only return object if at least one value is not null
            if (lowVal !== null || avgVal !== null || highVal !== null) {
              return {
                min: lowVal,
                avg: avgVal,
                max: highVal,
                status: avgStatusVal, // Keep for backward compatibility
                avgStatus: avgStatusVal,
                lowStatus: lowStatusVal,
                highStatus: highStatusVal
              };
            }
            return null;
          };

          return {
            id: zone.id,
            projectId: zone.project_id,
            projectName: zone.project_name,
            welding_system: zone.welding_system,
            jobNumber: zone.job_number,
            status: status,
            zone_status_by_instantaneuous: zone.zone_status_by_instantaneuous,
            avg_zone_status: zone.avg_zone_status,
            instantaneous_status: zone.instantaneous_status,
            stationName: `Station ${zone.station_number || '-'}`,
            bugType: zone.bug_type || '-',
            torch: zone.torch || '-',
            pass: zone.pass_name || '-',
            zone: zone.zone_name || '-',
            // M500 specific fields
            zone_name: zone.zone_name,
            m500_zone: zone.m500_zone,
            pipe_name: zone.pipe_name,
            pass_name: zone.pass_name,
            weldStartTime: zone.s_time ? formatDate(startDate) : '-',
            weldEndTime: zone.s_c_time ? zone.s_c_time : null,
            weldTime: handleNullValue(zone.weld_time),
            autoincrement: zone.auto_increment || '-',
            welduid_s_event_torch: zone.welduid_s_event_torch || null,
            bugNumber: zone.weld_uid || null,
            bandDiameter: handleNullValue(zone.band_diameter),
            wireDiameter: handleNullValue(zone.wire_diameter),
            loggingDistance: handleNullValue(zone.logging_distance),
            pipeDiameter: handleNullValue(zone.pipe_diameter),
            // Parameter data with min/avg/max values
            current: createParamObject(zone.current_low, zone.current_avg, zone.current_high, zone.current_avg_status, zone.current_low_status, zone.current_high_status),
            volt: createParamObject(zone.volts_low, zone.volts_avg, zone.volts_high, zone.volts_avg_status, zone.volts_low_status, zone.volts_high_status),
            travelSpeed: createParamObject(zone.travel_speed_low, zone.travel_speed_avg, zone.travel_speed_high, zone.travel_speed_avg_status, zone.travel_speed_low_status, zone.travel_speed_high_status),
            trueEnergy: createParamObject(zone.true_energy_low, zone.true_energy_avg, zone.true_energy_high, zone.true_energy_avg_status, zone.true_energy_low_status, zone.true_energy_high_status),
            heat: createParamObject(zone.heat_low, zone.heat_avg, zone.heat_high, zone.heat_avg_status, zone.heat_low_status, zone.heat_high_status),
            wireSpeed: createParamObject(zone.wire_speed_low, zone.wire_speed_avg, zone.wire_speed_high, zone.wire_speed_avg_status, zone.wire_speed_low_status, zone.wire_speed_high_status),
            frequency: createParamObject(zone.frequency_low, zone.frequency_avg, zone.frequency_high),
            target: createParamObject(zone.target_low, zone.target_avg, zone.target_high),
            horizontalBias: createParamObject(zone.horizontal_bias_low, zone.horizontal_bias_avg, zone.horizontal_bias_high),
            totalWireConsumed: createParamObject(zone.total_wire_consumed_low, zone.total_wire_consumed_avg, zone.total_wire_consumed_high),
            arcImpedance: createParamObject(zone.arc_impedance_low, zone.arc_impedance_avg, zone.arc_impedance_high),
            oscillationWidth: createParamObject(zone.oscillation_width_low, zone.oscillation_width_avg, zone.oscillation_width_high, zone.oscillation_width_avg_status, zone.oscillation_width_low_status, zone.oscillation_width_high_status),
            // Additional data from pass_view
            passView: zone.pass_view || {}
          };
        });

        return transformedZoneViewData;
      }
      return [];
    } catch (error) {
      throw error;
    }
  },

  fetchTiltView: async (projectId, projectName, weldNumber, jobNumber) => {
    try {
      let url = `/view/tilt?project_id=${projectId}${projectName ? `&project_name=${encodeURIComponent(projectName)}` : ''}`;
      if (weldNumber) {
        url += `&weld_number=${encodeURIComponent(weldNumber)}`;
      }
      if (jobNumber) {
        url += `&job_number=${encodeURIComponent(jobNumber)}`;
      }

      const res = await axiosConfig.get(url);
      if (res.status >= 200 && res.status < 300) {
        const tiltViewData = get(res, ['data', 'data', 'data'], []);
        // Transform API response to match frontend expected format
        const transformedTiltViewData = tiltViewData.map(tilt => {
          // Convert timestamps to readable format
          const startDate = new Date(tilt.s_time * 1000);

          const formatDate = (date) => {
            const day = String(date.getDate()).padStart(2, '0');
            const month = date.toLocaleDateString('en-US', { month: 'short' });
            const year = date.getFullYear();
            const hours = date.getHours();
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            const ampm = hours >= 12 ? 'PM' : 'AM';
            const displayHours = hours % 12 || 12;
            return `${day}-${month}-${year}, ${displayHours}:${minutes}:${seconds} ${ampm}`;
          };

          // Helper function to handle null values
          const handleNullValue = (value) => value !== null && value !== undefined ? value : null;
          // Helper function to create parameter object with null handling
          const createParamObject = (low, avg, high, avgStatus, lowStatus, highStatus) => {
            const lowVal = handleNullValue(low);
            const avgVal = handleNullValue(avg);
            const highVal = handleNullValue(high);
            const avgStatusVal = handleNullValue(avgStatus);
            const lowStatusVal = handleNullValue(lowStatus);
            const highStatusVal = handleNullValue(highStatus);

            if (lowVal !== null || avgVal !== null || highVal !== null) {
              return {
                min: lowVal,
                avg: avgVal,
                max: highVal,
                status: avgStatusVal, // Keep for backward compatibility
                avgStatus: avgStatusVal,
                lowStatus: lowStatusVal,
                highStatus: highStatusVal
              };
            }
            return null;
          };
          return {
            id: tilt.id,
            jobNumber: tilt.job_number,
            status: tilt.avg_tilt_status === 'TRUE' ? 'Completed' : (tilt.avg_tilt_status === 'FALSE' ? 'In Progress' : (tilt.avg_tilt_status === 'NULL' ? 'Rejected' : 'Not Started')),
            avg_staus_by_instantaneous: tilt.avg_staus_by_instantaneous,
            avg_tilt_status: tilt.avg_tilt_status,
            instantaneous_status: tilt.instantaneous_status,
            stationName: `Station ${tilt.station_number || '-'}`,
            bugType: tilt.bug_type || '-',
            torch: tilt.torch || '-',
            pass: tilt.pass_name || '-',
            tiltRange: `${tilt.tilt_start ?? '-'} - ${tilt.tilt_end ?? '-'}`,
            // Tilt parameters for API calls
            tilt_start: tilt.tilt_start,
            tilt_end: tilt.tilt_end,
            weldStartTime: tilt.s_time ? formatDate(startDate) : '-',
            weldEndTime: tilt.s_c_time ? tilt.s_c_time : null,
            weldTime: handleNullValue(tilt.weld_time),
            autoincrement: tilt.auto_increment || '-',
            welduid_s_event_torch: tilt.welduid_s_event_torch || null,
            bugNumber: tilt.weld_uid || null,
            bandDiameter: handleNullValue(tilt.band_diameter),
            wireDiameter: handleNullValue(tilt.wire_diameter),
            loggingDistance: handleNullValue(tilt.logging_distance),
            pipeDiameter: handleNullValue(tilt.pipe_diameter),
            current: createParamObject(tilt.current_low, tilt.current_avg, tilt.current_high, tilt.current_avg_status, tilt.current_low_status, tilt.current_high_status),
            volt: createParamObject(tilt.volts_low, tilt.volts_avg, tilt.volts_high, tilt.volts_avg_status, tilt.volts_low_status, tilt.volts_high_status),
            travelSpeed: createParamObject(tilt.travel_speed_low, tilt.travel_speed_avg, tilt.travel_speed_high, tilt.travel_speed_avg_status, tilt.travel_speed_low_status, tilt.travel_speed_high_status),
            trueEnergy: createParamObject(tilt.true_energy_low, tilt.true_energy_avg, tilt.true_energy_high, tilt.true_energy_avg_status, tilt.true_energy_low_status, tilt.true_energy_high_status),
            heat: createParamObject(tilt.heat_low, tilt.heat_avg, tilt.heat_high, tilt.heat_avg_status, tilt.heat_low_status, tilt.heat_high_status),
            wireSpeed: createParamObject(tilt.wire_speed_low, tilt.wire_speed_avg, tilt.wire_speed_high, tilt.wire_speed_avg_status, tilt.wire_speed_low_status, tilt.wire_speed_high_status),
            frequency: createParamObject(tilt.frequency_low, tilt.frequency_avg, tilt.frequency_high),
            target: createParamObject(tilt.target_low, tilt.target_avg, tilt.target_high),
            horizontalBias: createParamObject(tilt.horizontal_bias_low, tilt.horizontal_bias_avg, tilt.horizontal_bias_high),
            totalWireConsumed: createParamObject(tilt.total_wire_consumed_low, tilt.total_wire_consumed_avg, tilt.total_wire_consumed_high),
            arcImpedance: createParamObject(tilt.arc_impedance_low, tilt.arc_impedance_avg, tilt.arc_impedance_high),
            oscillationWidth: createParamObject(tilt.oscillation_width_low, tilt.oscillation_width_avg, tilt.oscillation_width_high, tilt.oscillation_width_avg_status, tilt.oscillation_width_low_status, tilt.oscillation_width_high_status),
            passView: tilt.pass_view || {}
          };
        });
        return transformedTiltViewData;
      }
      return [];
    } catch (error) {
      throw error;
    }
  },

  fetchPassViewDetails: async (projectId, projectName, welduid_s_event_torch, viewType, torchType) => {
    try {
      // URL encode all parameters to handle special characters properly
      const encodedProjectId = encodeURIComponent(projectId);
      const encodedProjectName = projectName ? encodeURIComponent(projectName) : '';
      const encodedWelduidEventTorch = encodeURIComponent(welduid_s_event_torch);
      const encodedViewType = encodeURIComponent(viewType);
      const encodedTorchType = encodeURIComponent(torchType);

      const url = `/log?project_id=${encodedProjectId}${encodedProjectName ? `&project_name=${encodedProjectName}` : ''}&welduid_s_event_torch=${encodedWelduidEventTorch}&view_type=${encodedViewType}&torch_type=${encodedTorchType}`;

      const res = await axiosConfig.get(url);

      if (res.status >= 200 && res.status < 300) {
        const passViewDetails = get(res, ['data'], {});
        return passViewDetails;
      }
      return {};
    } catch (error) {
      throw error;
    }
  },

  fetchWeldSegmentView: async (projectId, projectName, welduid_s_event_torch, viewType, torchType) => {
    try {
      const encodedProjectId = encodeURIComponent(projectId);
      const encodedProjectName = projectName ? encodeURIComponent(projectName) : '';
      const encodedWelduidEventTorch = encodeURIComponent(welduid_s_event_torch);
      const encodedViewType = encodeURIComponent(viewType);
      const encodedTorchType = encodeURIComponent(torchType);

      const url = `/view/weld-segment?project_id=${encodedProjectId}${encodedProjectName ? `&project_name=${encodedProjectName}` : ''}&welduid_s_event_torch=${encodedWelduidEventTorch}&view_type=${encodedViewType}&torch_type=${encodedTorchType}`;

      const res = await axiosConfig.get(url);

      if (res.status >= 200 && res.status < 300) {
        const weldSegmentView = get(res, ['data'], {});
        return weldSegmentView;
      }
      return {};
    } catch (error) {
      throw error;
    }
  },

  fetchWeldSegmentDetail: async (projectId, projectName, welduid_s_event_torch, welduid_t_event_torch, viewType, torchType) => {
    try {
      const encodedProjectId = encodeURIComponent(projectId);
      const encodedProjectName = projectName ? encodeURIComponent(projectName) : '';
      const encodedWelduidEventTorch = encodeURIComponent(welduid_s_event_torch);
      const encodedWelduidTEventTorch = encodeURIComponent(welduid_t_event_torch);
      const encodedViewType = encodeURIComponent(viewType);
      const encodedTorchType = encodeURIComponent(torchType);

      const url = `/view/weld-segment-detail?project_id=${encodedProjectId}${encodedProjectName ? `&project_name=${encodedProjectName}` : ''}&welduid_s_event_torch=${encodedWelduidEventTorch}&welduid_t_event_torch=${encodedWelduidTEventTorch}&view_type=${encodedViewType}&torch_type=${encodedTorchType}`;

      const res = await axiosConfig.get(url);

      if (res.status >= 200 && res.status < 300) {
        const weldSegmentDetail = get(res, ['data'], {});
        return weldSegmentDetail;
      }
      return {};
    } catch (error) {
      throw error;
    }
  },

  fetchTiltViewDetails: async (projectId, projectName, welduid_s_event_torch, viewType, torchType, tiltStart, tiltEnd) => {
    try {
      // URL encode all parameters to handle special characters properly
      const encodedProjectId = encodeURIComponent(projectId);
      const encodedProjectName = projectName ? encodeURIComponent(projectName) : '';
      const encodedWelduidEventTorch = encodeURIComponent(welduid_s_event_torch);
      const encodedViewType = encodeURIComponent(viewType);
      const encodedTorchType = encodeURIComponent(torchType);
      const encodedTiltStart = encodeURIComponent(tiltStart);
      const encodedTiltEnd = encodeURIComponent(tiltEnd);

      const url = `/log?project_id=${encodedProjectId}${encodedProjectName ? `&project_name=${encodedProjectName}` : ''}&welduid_s_event_torch=${encodedWelduidEventTorch}&view_type=${encodedViewType}&torch_type=${encodedTorchType}&tilt_start=${encodedTiltStart}&tilt_end=${encodedTiltEnd}`;

      const res = await axiosConfig.get(url);

      if (res.status >= 200 && res.status < 300) {
        const tiltViewDetails = get(res, ['data'], {});
        return tiltViewDetails;
      }
      return {};
    } catch (error) {
      throw error;
    }
  },

  fetchZoneViewDetails: async (projectId, projectName, welduid_s_event_torch, viewType, torchType, zoneName) => {
    try {
      // URL encode all parameters to handle special characters properly
      const encodedProjectId = encodeURIComponent(projectId);
      const encodedProjectName = projectName ? encodeURIComponent(projectName) : '';
      const encodedWelduidEventTorch = encodeURIComponent(welduid_s_event_torch);
      const encodedViewType = encodeURIComponent(viewType);
      const encodedTorchType = encodeURIComponent(torchType);
      const encodedZoneName = encodeURIComponent(zoneName);

      const url = `/log?project_id=${encodedProjectId}${encodedProjectName ? `&project_name=${encodedProjectName}` : ''}&welduid_s_event_torch=${encodedWelduidEventTorch}&view_type=${encodedViewType}&torch_type=${encodedTorchType}&zone_name=${encodedZoneName}`;

      const res = await axiosConfig.get(url);

      if (res.status >= 200 && res.status < 300) {
        const zoneViewDetails = get(res, ['data'], {});
        return zoneViewDetails;
      }
      return {};
    } catch (error) {
      throw error;
    }
  },

  // New API function to fetch all projects from data360 live
  fetchProjects: async () => {
    try {
      // Starting API call to data360 live

      // Check if we're in development mode and should use mock data
      const useMockData = process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_MOCK_PROJECTS === 'true';

      if (useMockData) {
        // Development mode - using mock data
        const mockProjects = [
          {
            project_id: 1,
            project_name: "Demo Project",
            project_number: "P001",
            customer_name: "Demo Customer",
            welding_systems: ["m500", "p625"],
            pipe_thickness: 4,
            pipe_diameter: 5,
            pipe_material: "X"
          },
          {
            project_id: 2,
            project_name: "Test Project",
            project_number: "P002",
            customer_name: "Test Customer",
            welding_systems: ["p600z"],
            pipe_thickness: 6,
            pipe_diameter: 8,
            pipe_material: "Y"
          },
          {
            project_id: 3,
            project_name: "Production Project",
            project_number: "P003",
            customer_name: "Production Customer",
            welding_systems: ["m500", "p625", "p600z"],
            pipe_thickness: 8,
            pipe_diameter: 12,
            pipe_material: "Z"
          }
        ];

        // Mock projects created
        return mockProjects;
      }

      // Try using the existing axiosConfig first (which should handle CORS properly)
      // Attempting with configured axios

      try {
        const res = await axiosConfig.get('/project/get-projects');
        // Success with configured axios

        if (res.status >= 200 && res.status < 300) {
          // Extract projects from the response structure
          const projects = get(res, ['data', 'data', 'projects'], []);
          // Extracted projects array

          // Log each project for debugging
          if (projects && projects.length > 0) {
            // Project details logged
            projects.forEach((project, index) => {
              // Project details logged
            });
          } else {
            // No projects found in response
          }

          return projects;
        } else {
          // API returned non-200 status
          return [];
        }
      } catch (configError) {
        // Configured axios failed, trying direct axios

        // Fallback to direct axios call with CORS headers
        const res = await axios.get(`${process.env.REACT_APP_LIMITS_API_BASE_URL}/project/get-projects`, {
          headers: {
            'accept': 'application/json',
            'accept-language': 'en-GB,en-US;q=0.9,en;q=0.8',
            'cache-control': 'no-cache',
            'origin': 'https://dlnueyz69lbn5.cloudfront.net',
            'pragma': 'no-cache',
            'referer': 'https://dlnueyz69lbn5.cloudfront.net/',
            'sec-ch-ua': '"Not)A;Brand";v="8", "Chromium";v="138", "Google Chrome";v="138"',
            'sec-ch-ua-mobile': '?0',
            'sec-ch-ua-platform': '"macOS"',
            'sec-fetch-dest': 'empty',
            'sec-fetch-mode': 'cors',
            'sec-fetch-site': 'cross-site',
            'user-agent': 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/138.0.0.0 Safari/537.36'
          }
        });

        // Success with direct axios

        if (res.status >= 200 && res.status < 300) {
          // Extract projects from the response structure
          const projects = get(res, ['data', 'data', 'projects'], []);
          // Extracted projects array

          // Log each project for debugging
          if (projects && projects.length > 0) {
            // Project details logged
            projects.forEach((project, index) => {
              // Project details logged
            });
          } else {
            // No projects found in response
          }

          return projects;
        } else {
          // API returned non-200 status
          return [];
        }
      }
    } catch (error) {
      // Check if it's a CORS error
      if (error.message.includes('CORS') || error.message.includes('cors')) {
        // CORS Error detected! This means the API server needs to allow cross-origin requests.
        // Solutions:
        // 1. Configure the API server to allow CORS
        // 2. Use a proxy server
        // 3. Make the request from the same origin

        // For development/testing, provide mock data
        // Providing mock data for development
        const mockProjects = [
          {
            project_id: 1,
            project_name: "Demo Project",
            project_number: "P001",
            customer_name: "Demo Customer",
            welding_systems: ["m500", "p625"],
            pipe_thickness: 4,
            pipe_diameter: 5,
            pipe_material: "X"
          },
          {
            project_id: 2,
            project_name: "Test Project",
            project_number: "P002",
            customer_name: "Test Customer",
            welding_systems: ["p600z"],
            pipe_thickness: 6,
            pipe_diameter: 8,
            pipe_material: "Y"
          },
          {
            project_id: 3,
            project_name: "Production Project",
            project_number: "P003",
            customer_name: "Production Customer",
            welding_systems: ["m500", "p625", "p600z"],
            pipe_thickness: 8,
            pipe_diameter: 12,
            pipe_material: "Z"
          }
        ];

        // Mock projects created
        return mockProjects;
      }

      throw error;
    }
  },

  // Report generation API functions
  generateReport: async (reportType, projectId, emailAddress, weldNumbers = null, startDate = null, endDate = null) => {
    try {
      let url = '';
      let params = new URLSearchParams();

      params.append('projectId', projectId);

      // Only add emailAddress if it's provided and not the default noreply email
      if (emailAddress && emailAddress !== 'noreply@system.com' && emailAddress.trim() !== '') {
        params.append('emailAddress', emailAddress);
      }

      if (weldNumbers && weldNumbers.length > 0) {
        params.append('weldNumbers', weldNumbers.join(','));
      }
      if (startDate) {
        params.append('startDate', startDate);
      }

      if (endDate) {
        params.append('endDate', endDate);
      }

      switch (reportType) {
        case 't-logs':
          url = `/reports/t-logs?${params.toString()}`;
          break;
        case 'pass-view':
          url = `/reports/download-proxy/pass-view?${params.toString()}`;
          break;
        case 'zone-view':
          url = `/reports/download-proxy/zone-view?${params.toString()}`;
          break;
        default:
          throw new Error(`Unsupported report type: ${reportType}`);
      }

      const response = await axiosConfig.get(url);

      // Accept common success status codes: 200 (OK), 201 (Created), 202 (Accepted)
      if (response.status >= 200 && response.status < 300) {
        if (response.status === 202) {
          // Report generation accepted (HTTP 202): Report is being processed asynchronously
        } else if (response.status === 201) {
          // Report generation created (HTTP 201): Report has been created successfully
        } else {
          // Report generation successful
        }
        return response.data;
      }

      throw new Error(`HTTP error! status: ${response.status}`);
    } catch (error) {
      // Handle 500 errors that Axios throws automatically
      if (error.response && error.response.status === 500) {
        const response = error.response;
        const errorMessage = response.data?.data?.data?.message ||
          response.data?.data?.message ||
          response.data?.message ||
          'Report generation failed';

        const customError = new Error(errorMessage);
        customError.statusCode = 500;
        customError.requiresEmail = true;
        customError.txId = response.data.txId;
        customError.nestedTxId = response.data?.data?.txId;
        throw customError;
      }

      // Handle custom error objects that already have the 500 structure
      if (error.statusCode === 500) {
        const errorMessage = error.data?.data?.data?.message ||
          error.data?.data?.message ||
          error.data?.message ||
          'Report generation failed';

        const customError = new Error(errorMessage);
        customError.statusCode = 500;
        customError.requiresEmail = true;
        customError.txId = error.txId;
        customError.nestedTxId = error.data?.data?.txId;
        throw customError;
      }

      throw error;
    }
  },

  // New v2 report generation API function that returns S3 URL
  generateReportV2: async (reportType, projectId, emailAddress, weldNumbers = null, startDate = null, endDate = null) => {
    try {
      let url = '';
      let params = new URLSearchParams();

      // console.log('🔄 generateReportV2 - weldNumbers', weldNumbers);
      params.append('projectId', projectId);
      // Only append emailAddress if it's provided and not the default noreply email
      if (emailAddress && emailAddress !== 'noreply@system.com' && emailAddress.trim() !== '') {
        params.append('emailAddress', emailAddress);
      }

      if (weldNumbers && weldNumbers.length > 0) {
        params.append('weldNumbers', weldNumbers.join(','));
      }
      if (startDate) {
        params.append('startDate', startDate);
      }

      if (endDate) {
        params.append('endDate', endDate);
      }

      switch (reportType) {
        case 't-logs':
          // T-logs still uses v1 API
          url = `/reports/t-logs?${params.toString()}`;
          break;
        case 'pass-view':
          url = `/reports/download-proxy/pass-view?${params.toString()}`;
          break;
        case 'zone-view':
          url = `/reports/download-proxy/zone-view?${params.toString()}`;
          break;
        default:
          throw new Error(`Unsupported report type: ${reportType}`);
      }

      // Use v2 axios config for pass-view and zone-view, v1 for t-logs
      const axiosInstance = reportType === 't-logs' ? axiosConfig : axiosConfigV2;


      const response = await axiosInstance.get(url);

      // Accept common success status codes: 200 (OK), 201 (Created), 202 (Accepted)
      if (response.status >= 200 && response.status < 300) {
        // Handle nested success response structure
        const nestedData = response.data?.data?.data;
        const downloadUrl = nestedData?.data?.downloadUrl;


        return {
          ...response.data,
          isV2: reportType !== 't-logs', // Mark as v2 for pass-view and zone-view
          reportType,
          downloadUrl: downloadUrl, // Extract download URL for easy access
          success: true,
          // Also store the nested data for fallback access
          nestedData: nestedData
        };
      }

      // Handle 500 status code for long-running reports that require email
      if (response.status === 500) {
        // Handle nested response structure - check multiple possible locations for the message
        const errorMessage = response.data?.data?.data?.message ||
          response.data?.data?.message ||
          response.data?.message ||
          'Report generation failed';

        const error = new Error(errorMessage);
        error.statusCode = 500;
        error.requiresEmail = true;
        error.txId = response.data.txId;
        error.nestedTxId = response.data?.data?.txId;
        throw error;
      }

      throw new Error(`HTTP error! status: ${response.status}`);
    } catch (error) {
      // Handle 500 errors that Axios throws automatically
      if (error.response && error.response.status === 500) {
        const response = error.response;
        const errorMessage = response.data?.data?.data?.message ||
          response.data?.data?.message ||
          response.data?.message ||
          'Report generation failed';

        const customError = new Error(errorMessage);
        customError.statusCode = 500;
        customError.requiresEmail = true;
        customError.txId = response.data.txId;
        customError.nestedTxId = response.data?.data?.txId;
        throw customError;
      }

      // Handle custom error objects that already have the 500 structure
      if (error.statusCode === 500) {
        const errorMessage = error.data?.data?.data?.message ||
          error.data?.data?.message ||
          error.data?.message ||
          'Report generation failed';

        const customError = new Error(errorMessage);
        customError.statusCode = 500;
        customError.requiresEmail = true;
        customError.txId = error.txId;
        customError.nestedTxId = error.data?.data?.txId;
        throw customError;
      }

      throw error;
    }
  },

  fetchLimitsData: async (projectId) => {
    try {
      const url = `/limits/get-limits?project_id=${projectId}`;
      const res = await axiosConfig.get(url);

      if (res.status >= 200 && res.status < 300) {
        const data = get(res, ['data'], {});

        if (data.success === 1 && data.data) {
          return data.data;
        } else {
          // No limits data found in response
          return { allDataSet: [], weldDetails: [], projectData: {} };
        }
      } else {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
    } catch (error) {
      throw error;
    }
  },

  updateStatusConfiguration: async (projectId, payload) => {
    try {
      const url = `/limits/edit-status-configuration?project_id=${projectId}`;
      const res = await axiosConfig.put(url, payload);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  fetchManualStatusConfiguration: async (projectId, jobNumber = null) => {
    try {
      let url = `/limits/get-manual-status-configuration?project_id=${projectId}`;
      if (jobNumber) {
        url += `&job_number=${encodeURIComponent(jobNumber)}`;
      }
      const res = await axiosConfig.get(url);
      if (res.status >= 200 && res.status < 300 && res.data?.success === 1) {
        return res.data.data;
      }
      return { jobNumbers: [], configurations: [] };
    } catch (error) {
      throw error;
    }
  },

  updateManualStatusConfiguration: async (projectId, payload) => {
    try {
      const url = `/limits/edit-manual-status-configuration?project_id=${projectId}`;
      const res = await axiosConfig.put(url, payload);
      if (res.status >= 200 && res.status < 300) {
        return res.data?.data ?? res.data;
      }
      throw new Error('Failed to save manual status configuration');
    } catch (error) {
      throw error;
    }
  },

  updateWeldNumber: async (payload) => {
    try {

      let apipayload = null;
      let url = null;
      if (!payload.isNewWeld && payload.weld_uid) {
        // Pass / Zone / Tilt: send only the selected row's single job number
        const toSingleJobNumber = (value) => {
          if (value == null || value === '') return '';
          if (Array.isArray(value)) {
            return value.map((v) => String(v ?? '').trim()).find(Boolean) || '';
          }
          return String(value)
            .split(/[,;|]/)
            .map((s) => s.trim())
            .find(Boolean) || '';
        };
        const selectedJobNumber =
          toSingleJobNumber(payload.oldJobNumber ?? payload.old_job_number);
        const selectedNewJobNumber =
          toSingleJobNumber(payload.newJobNumber ?? payload.new_job_number) || selectedJobNumber;

        url = `/weld-status/change-weld-number-with-uid`;
        apipayload = {
          project_id: payload.project_id,
          old_weld_number: payload.old_weld_number,
          new_weld_number: payload.new_weld_number,
          old_job_number: selectedJobNumber,
          new_job_number: selectedNewJobNumber,
          weld_uid: payload.weld_uid
        };
      }
      else if (payload.isNewWeld) {
        url = `/weld-status/change-weld-number`;
        const splitJobNumbers = (value) => {
          if (value == null || value === '') return [];
          if (Array.isArray(value)) {
            return value.map((v) => String(v ?? '').trim()).filter(Boolean);
          }
          return String(value)
            .split(/[,;|]/)
            .map((s) => s.trim())
            .filter(Boolean);
        };
        const oldJobs = splitJobNumbers(payload.oldJobNumber);
        const newJobs = splitJobNumbers(payload.newJobNumber);
        const jobCount = Math.max(oldJobs.length, newJobs.length, 1);

        let lastData = null;
        for (let i = 0; i < jobCount; i++) {
          const old_job_number = oldJobs[i] ?? oldJobs[0] ?? '';
          const new_job_number = newJobs[i] ?? newJobs[0] ?? old_job_number;
          const res = await axiosConfig.post(url, {
            project_id: payload.project_id,
            old_weld_number: payload.old_weld_number,
            new_weld_number: payload.new_weld_number,
            old_job_number,
            new_job_number,
          });
          if (!(res.status >= 200 && res.status < 300)) {
            throw new Error('Failed to update weld number');
          }
          lastData = res.data;
        }
        Notification.success('Weld number updated successfully');
        return lastData;
      }
      if (!apipayload || !url)
        throw new Error('Invalid payload or url for update weld number');

      const res = await axiosConfig.post(url, apipayload);

      if (res.status >= 200 && res.status < 300) {
        Notification.success('Weld number updated successfully');
        return res.data;
      }

      throw new Error('Failed to update weld number');
    } catch (error) {
      console.error('❌ Saga - updateWeldNumber error:', error);
      throw error;
    }
  },

  renameJobNumber: async (payload) => {
    try {
      const url = `/limits/rename-jobnumber`;
      const apiPayload = {
        project_id: payload.project_id,
        old_job_number: payload.old_job_number,
        new_job_number: payload.new_job_number,
      };

      const res = await axiosConfig.put(url, apiPayload);

      if (res.status >= 200 && res.status < 300) {
        Notification.success('Job number renamed successfully');
        return res.data;
      }

      throw new Error('Failed to rename job number');
    } catch (error) {
      console.error('❌ Saga - renameJobNumber error:', error);
      throw error;
    }
  }
};

// Track ongoing API calls to prevent duplicates
const ongoingCalls = new Map();

// Helper to check if API call is already in progress
function* callWithDeduplication(key, apiCall, ...args) {
  // Check if call is already in progress
  if (ongoingCalls.has(key)) {
    return null;
  }

  // Mark call as in progress
  ongoingCalls.set(key, true);

  try {
    const result = yield call(apiCall, ...args);
    return result;
  } finally {
    // Always remove from map when done
    ongoingCalls.delete(key);
  }
}

// Sagas
function* fetchWeldDataSaga(action) {
  const jobKey = Array.isArray(action.jobNumber)
    ? action.jobNumber.join(',')
    : action.jobNumber ?? '';
  const pipeKey = Array.isArray(action.pipeIds) && action.pipeIds.length
    ? action.pipeIds.join(',')
    : (action.pipeId ?? '');
  const callKey = `weld-data-${action.projectId}${jobKey ? `-${jobKey}` : ''}${pipeKey ? `-pipe-${pipeKey}` : ''}`;

  try {
    const weldData = yield callWithDeduplication(
      callKey,
      api.fetchWeldData,
      action.projectId,
      action.projectName,
      action.jobNumber,
      action.pipeId,
      action.pipeIds,
    );

    if (weldData === null) {
      return;
    }

    yield put(setWeldData(weldData.weldData, weldData.statusCalculationMethod));
    if (Array.isArray(weldData.partialErrors) && weldData.partialErrors.length > 0) {
      const summary = weldData.partialErrors
        .map((e) => (e.pipeId ? `pipe ${e.pipeId}` : 'pipe') + `: ${e.error}`)
        .join('; ');
      Notification.warning(`Some pipes failed to load: ${summary}`);
    }
  } catch (error) {
    console.error('❌ fetchWeldDataSaga - Error fetching weld data:', error);
    const errorMessage = error.message || 'Failed to fetch weld data';
    yield put(setWeldDataFailure(errorMessage));

    // Show error toast notification
    if (error.response?.status >= 500) {
      Notification.error('Server error: Unable to load weld data. Please try again later.');
    } else if (error.response?.status === 404) {
      Notification.error('Weld data not found for this project.');
    } else if (error.response?.status >= 400) {
      Notification.error('Bad request: Unable to load weld data. Please check your inputs.');
    } else {
      Notification.error(`Error loading weld data: ${errorMessage}`);
    }
  }
}

function* fetchWeldPassesSaga(action) {
  const callKey = `weld-passes-${action.projectId}-${action.weldNumber}`;

  try {
    const weldPasses = yield callWithDeduplication(
      callKey,
      api.fetchWeldPasses,
      action.projectId,
      action.projectName,
      action.weldNumber,
      action.jobNumber
    );

    if (weldPasses === null) {
      return;
    }

    yield put(setWeldPasses(weldPasses));
  } catch (error) {
    console.error('❌ Error fetching weld passes:', error);
    const errorMessage = error.message || 'Failed to fetch weld passes';
    yield put(setWeldPassesFailure(errorMessage));

    // Show error toast notification
    if (error.response?.status >= 500) {
      Notification.error('Server error: Unable to load pass view data. Please try again later.');
    } else if (error.response?.status === 404) {
      Notification.error('Pass data not found for this weld.');
    } else {
      Notification.error(`Error loading pass view: ${errorMessage}`);
    }
  }
}

function* fetchZoneViewSaga(action) {
  const callKey = `zone-view-${action.projectId}-${action.weldNumber}`;

  try {
    const zoneViewData = yield callWithDeduplication(
      callKey,
      api.fetchZoneView,
      action.projectId,
      action.projectName,
      action.weldNumber,
      action.jobNumber
    );

    if (zoneViewData === null) {
      return;
    }

    yield put(setZoneView(zoneViewData));
  } catch (error) {
    console.error('❌ Error fetching zone view:', error);
    const errorMessage = error.message || 'Failed to fetch zone view';
    yield put(setZoneViewFailure(errorMessage));

    // Show error toast notification
    if (error.response?.status >= 500) {
      Notification.error('Server error: Unable to load zone view data. Please try again later.');
    } else if (error.response?.status === 404) {
      Notification.error('Zone data not found for this weld.');
    } else {
      Notification.error(`Error loading zone view: ${errorMessage}`);
    }
  }
}

function* fetchTiltViewSaga(action) {
  const callKey = `tilt-view-${action.projectId}-${action.weldNumber}`;

  try {
    const tiltViewData = yield callWithDeduplication(
      callKey,
      api.fetchTiltView,
      action.projectId,
      action.projectName,
      action.weldNumber,
      action.jobNumber
    );

    if (tiltViewData === null) {
      return;
    }

    yield put(setTiltView(tiltViewData));
  } catch (error) {
    console.error('❌ Error fetching tilt view:', error);
    const errorMessage = error.message || 'Failed to fetch tilt view';
    yield put(setTiltViewFailure(errorMessage));

    // Show error toast notification
    if (error.response?.status >= 500) {
      Notification.error('Server error: Unable to load tilt view data. Please try again later.');
    } else if (error.response?.status === 404) {
      Notification.error('Tilt data not found for this weld.');
    } else {
      Notification.error(`Error loading tilt view: ${errorMessage}`);
    }
  }
}

function* fetchPassViewDetailsSaga(action) {
  const callKey = `pass-details-${action.projectId}-${action.welduid_s_event_torch}`;

  try {
    const passViewDetails = yield callWithDeduplication(
      callKey,
      api.fetchPassViewDetails,
      action.projectId,
      action.projectName,
      action.welduid_s_event_torch,
      action.viewType,
      action.torchType
    );

    if (passViewDetails === null) {
      return;
    }

    yield put(setPassViewDetails(passViewDetails));
  } catch (error) {
    console.error('❌ Error fetching pass view details:', error);
    const errorMessage = error.message || 'Failed to fetch pass view details';
    yield put(setPassViewDetailsFailure(errorMessage));

    // Show error toast notification
    if (error.response?.status >= 500) {
      Notification.error('Server error: Unable to load pass details. Please try again later.');
    } else {
      Notification.error(`Error loading pass details: ${errorMessage}`);
    }
  }
}

function* fetchWeldSegmentViewSaga(action) {
  const callKey = `weld-segment-view-${action.projectId}-${action.welduid_s_event_torch}`;

  try {
    const weldSegmentView = yield callWithDeduplication(
      callKey,
      api.fetchWeldSegmentView,
      action.projectId,
      action.projectName,
      action.welduid_s_event_torch,
      action.viewType,
      action.torchType
    );

    if (weldSegmentView === null) {
      return;
    }

    yield put(setWeldSegmentView(weldSegmentView));
  } catch (error) {
    console.error('❌ Error fetching weld segment view:', error);
    const errorMessage = error.message || 'Failed to fetch weld segment view';
    yield put(setWeldSegmentViewFailure(errorMessage));

    if (error.response?.status >= 500) {
      Notification.error('Server error: Unable to load weld segments. Please try again later.');
    } else {
      Notification.error(`Error loading weld segments: ${errorMessage}`);
    }
  }
}

function* fetchWeldSegmentDetailSaga(action) {
  const callKey = `weld-segment-detail-${action.projectId}-${action.welduid_s_event_torch}-${action.welduid_t_event_torch}`;

  try {
    const weldSegmentDetail = yield callWithDeduplication(
      callKey,
      api.fetchWeldSegmentDetail,
      action.projectId,
      action.projectName,
      action.welduid_s_event_torch,
      action.welduid_t_event_torch,
      action.viewType,
      action.torchType
    );

    if (weldSegmentDetail === null) {
      return;
    }

    yield put(setWeldSegmentDetail(weldSegmentDetail));
  } catch (error) {
    console.error('❌ Error fetching weld segment detail:', error);
    const errorMessage = error.message || 'Failed to fetch weld segment detail';
    yield put(setWeldSegmentDetailFailure(errorMessage));

    if (error.response?.status >= 500) {
      Notification.error('Server error: Unable to load segment details. Please try again later.');
    } else {
      Notification.error(`Error loading segment details: ${errorMessage}`);
    }
  }
}

function* fetchTiltViewDetailsSaga(action) {
  const callKey = `tilt-details-${action.projectId}-${action.welduid_s_event_torch}-${action.tiltStart}-${action.tiltEnd}`;

  try {
    const tiltViewDetails = yield callWithDeduplication(
      callKey,
      api.fetchTiltViewDetails,
      action.projectId,
      action.projectName,
      action.welduid_s_event_torch,
      action.viewType,
      action.torchType,
      action.tiltStart,
      action.tiltEnd
    );

    if (tiltViewDetails === null) {
      return;
    }

    yield put(setPassViewDetails(tiltViewDetails)); // Reuse the same action for now
  } catch (error) {
    console.error('❌ Error fetching tilt view details:', error);
    const errorMessage = error.message || 'Failed to fetch tilt view details';
    yield put(setPassViewDetailsFailure(errorMessage));

    // Show error toast notification
    if (error.response?.status >= 500) {
      Notification.error('Server error: Unable to load tilt details. Please try again later.');
    } else {
      Notification.error(`Error loading tilt details: ${errorMessage}`);
    }
  }
}

function* fetchZoneViewDetailsSaga(action) {
  const callKey = `zone-details-${action.projectId}-${action.welduid_s_event_torch}-${action.zoneName}`;

  try {
    const zoneViewDetails = yield callWithDeduplication(
      callKey,
      api.fetchZoneViewDetails,
      action.projectId,
      action.projectName,
      action.welduid_s_event_torch,
      action.viewType,
      action.torchType,
      action.zoneName
    );

    if (zoneViewDetails === null) {
      return;
    }

    yield put(setZoneViewDetails(zoneViewDetails));
  } catch (error) {
    console.error('❌ Error fetching zone view details:', error);
    const errorMessage = error.message || 'Failed to fetch zone view details';
    yield put(setZoneViewDetailsFailure(errorMessage));

    // Show error toast notification
    if (error.response?.status >= 500) {
      Notification.error('Server error: Unable to load zone details. Please try again later.');
    } else {
      Notification.error(`Error loading zone details: ${errorMessage}`);
    }
  }
}

// New saga for fetching projects
function* fetchProjectsSaga() {
  try {
    const projects = yield call(api.fetchProjects);
    yield put(setProjects(projects));
  } catch (error) {
    yield put(setProjectsFailure(error.message));
  }
}

// Saga for fetching slog data
function* fetchSlogsSaga(action) {
  try {
    const { projectId, projectName } = action.payload;
    const slogData = yield call(api.fetchSlogsData, projectId, projectName);
    yield put(setSlogsData(slogData));
  } catch (error) {
    yield put(setSlogsDataFailure(error.message));
  }
}

// Saga for report generation
function* generateReportSaga(action) {
  try {
    const { reportType, projectId, emailAddress, startDate, endDate, reportVersion = 'v1' } = action.payload;

    // Get the first email address if multiple are provided
    const email = Array.isArray(emailAddress) ? emailAddress[0] : emailAddress;

    // Check if weldNumbers exists in payload before using it
    const weldNumbers = action.payload.weldNumbers;

    // Choose API function based on version and report type
    let apiFunction = api.generateReport; // Default to v1

    if (reportVersion === 'v2' && (reportType === 'pass-view' || reportType === 'zone-view')) {
      apiFunction = api.generateReportV2;
    }

    const reportData = yield call(
      apiFunction,
      reportType,
      projectId,
      email,
      weldNumbers, // This will be undefined if not in payload
      startDate,
      endDate
    );

    yield put(setReportGenerationSuccess(reportData));
  } catch (error) {
    yield put(setReportGenerationFailure(error.message));
  }
}

function* fetchLimitsDataSaga(action) {
  try {
    const limitsData = yield call(api.fetchLimitsData, action.projectId);
    yield put(setLimitsData(limitsData));
  } catch (error) {
    yield put(setLimitsDataFailure(error.message));
  }
}

function* updateStatusConfigurationSaga(action) {
  try {
    const { projectId, payload } = action.payload;
    const result = yield call(api.updateStatusConfiguration, projectId, payload);
    yield put(updateStatusConfigurationSuccess(result));
  } catch (error) {
    yield put(updateStatusConfigurationFailure(error.message));
  }
}

function* fetchManualStatusConfigurationSaga(action) {
  try {
    const result = yield call(
      api.fetchManualStatusConfiguration,
      action.projectId,
      action.jobNumber
    );
    yield put(setManualStatusConfiguration(result));
  } catch (error) {
    yield put(setManualStatusConfigurationFailure(error.message || 'Failed to load manual status configuration'));
  }
}

function* updateManualStatusConfigurationSaga(action) {
  try {
    const payloads = Array.isArray(action.payload) ? action.payload : [action.payload];
    for (const payload of payloads) {
      yield call(
        api.updateManualStatusConfiguration,
        action.projectId,
        payload
      );
    }
    const fullResult = yield call(
      api.fetchManualStatusConfiguration,
      action.projectId
    );
    yield put(updateManualStatusConfigurationSuccess(fullResult));
    const jobCount = payloads.length;
    Notification.success(
      jobCount > 1
        ? `Manual status configuration saved for ${jobCount} job numbers`
        : 'Manual status configuration saved successfully'
    );
  } catch (error) {
    const message = error.response?.data?.message || error.message || 'Failed to save manual status configuration';
    yield put(updateManualStatusConfigurationFailure(message));
    Notification.error('Failed to save manual status configuration', message);
  }
}

function* assignPipeToWeldSaga(action) {
  try {
    const payload = action.payload;
    const result = yield call(api.assignPipeToWeld, payload);
    yield put(assignPipeToWeldSuccess(result));

    // Reload weld data after successful assignment to reflect the changes
    if (payload.project_id) {
      // Wait a bit before reloading to ensure backend has processed the update
      // yield delay(500);
      // yield put(fetchWeldData(payload.project_id, null, null, payload.pipe_details_id));
    }
  } catch (error) {
    console.error('❌ assignPipeToWeldSaga - Error assigning pipe to weld:', error);
    const errorMessage = error.message || 'Failed to assign pipe to weld';
    yield put(assignPipeToWeldFailure(errorMessage));

    // Show error toast notification
    if (error.response?.status >= 500) {
      Notification.error('Server error: Unable to assign pipe to weld. Please try again later.');
    } else if (error.response?.status >= 400) {
      Notification.error('Bad request: Unable to assign pipe to weld. Please check your inputs.');
    } else {
      Notification.error(`Error assigning pipe to weld: ${errorMessage}`);
    }
  }
}

function* assignJobNumberToPipeSaga(action) {
  try {
    const payload = action.payload;
    const result = yield call(api.assignJobNumberToPipe, payload);
    yield put(assignJobNumberToPipeSuccess(result));
  } catch (error) {
    yield put(assignJobNumberToPipeFailure(error.message));
  }
}

function* updateWeldNumberSaga(action) {
  try {
    const payload = action.payload;
    const result = yield call(api.updateWeldNumber, payload);
    yield put(updateWeldNumberSuccess(result));

    // Reload weld data after successful update to reflect the changes
    if (payload.project_id) {
      // Get current project info from state to refresh weld data
      const state = yield select((state) => state.weldData);
      const currentProject = state.currentProject;
      const projectName = currentProject?.project?.projectName || null;

      // Note: jobNumber and pipeId are not available in payload anymore,
      // but fetchWeldData can work with just projectId and projectName
      //yield put(fetchWeldData(payload.project_id, projectName, null, null));
    }
  } catch (error) {
    console.error('❌ updateWeldNumberSaga - Error updating weld number:', error);
    const errorMessage = error.message || 'Failed to update weld number';
    yield put(updateWeldNumberFailure(errorMessage));

    // Show error toast notification
    if (error.response?.status >= 500) {
      Notification.error('Server error: Unable to update weld number. Please try again later.');
    } else if (error.response?.status >= 400) {
      Notification.error('Bad request: Unable to update weld number. Please check your inputs.');
    } else {
      Notification.error(`Error updating weld number: ${errorMessage}`);
    }
  }
}

function* renameJobNumberSaga(action) {
  try {
    const payload = action.payload;
    const result = yield call(api.renameJobNumber, payload);
    yield put(renameJobNumberSuccess(result));
  } catch (error) {
    console.error('❌ renameJobNumberSaga - Error renaming job number:', error);
    const errorMessage = error.message || 'Failed to rename job number';
    yield put(renameJobNumberFailure(errorMessage));

    if (error.response?.status >= 500) {
      Notification.error('Server error: Unable to rename job number. Please try again later.');
    } else if (error.response?.status >= 400) {
      Notification.error('Bad request: Unable to rename job number. Please check your inputs.');
    } else {
      Notification.error(`Error renaming job number: ${errorMessage}`);
    }
  }
}

function* mergePassesSaga(action) {
  try {
    const payload = action.payload;
    const result = yield call(api.updateWelduidSEventTorch, payload);
    yield put(mergePassesSuccess(result));

    Notification.success('Passes merged successfully');

    // Refresh weld passes data after successful merge
    // if (payload.project_id && payload.weldNumber) {
    //   const state = yield select((state) => state.weldData);
    //   const currentProject = state.currentProject;
    //   const projectName = currentProject?.project?.projectName || null;
    //   const jobNumber = payload.jobNumber || null;

    //  // yield put(fetchWeldPasses(payload.project_id, projectName, payload.weldNumber, jobNumber));
    // }
  } catch (error) {
    console.error('❌ mergePassesSaga - Error merging passes:', error);
    const errorMessage = error.message || 'Failed to merge passes';
    yield put(mergePassesFailure(errorMessage));

    // Show error toast notification
    if (error.response?.status >= 500) {
      Notification.error('Server error: Unable to merge passes. Please try again later.');
    } else if (error.response?.status >= 400) {
      Notification.error('Bad request: Unable to merge passes. Please check your inputs.');
    } else {
      Notification.error(`Error merging passes: ${errorMessage}`);
    }
  }
}

function* restoreMergedPassesSaga(action) {
  try {
    const payload = action.payload;
    const result = yield call(api.restoreWelduidSEventTorch, payload);
    yield put(restoreMergedPassesSuccess(result));

    // Notification.success('Passes unmerged successfully');

    // if (payload.project_id && payload.weldNumber) {
    //   const state = yield select((state) => state.weldData);
    //   const currentProject = state.currentProject;
    //   const projectName = currentProject?.project?.projectName || null;
    //   const jobNumber = payload.jobNumber || null;

    //   yield put(fetchWeldPasses(payload.project_id, projectName, payload.weldNumber, jobNumber));
    // }
  } catch (error) {
    console.error('❌ restoreMergedPassesSaga - Error restoring passes:', error);

    // Extract error message from response
    let errorMessage = error.message || 'Failed to restore passes';
    const responseMessage = error.response?.data?.data?.message || error.response?.data?.message;

    // Check for specific "No merge history records" error
    if (responseMessage && responseMessage.includes('No merge history records')) {
      errorMessage = 'No merge history records found';
    } else if (responseMessage) {
      errorMessage = responseMessage;
    }

    yield put(restoreMergedPassesFailure(errorMessage));

    if (error.response?.status >= 500) {
      Notification.error('Server error: Unable to unmerge passes. Please try again later.');
    } else if (error.response?.status >= 400) {
      // Show specific message for "No merge history records"
      if (errorMessage.includes('No merge history records')) {
        Notification.error('No merge history records found');
      } else {
        Notification.error('Bad request: Unable to unmerge passes. Please check your inputs.');
      }
    } else {
      Notification.error(`Error unmerging passes: ${errorMessage}`);
    }
  }
}

// Root saga
export function* weldDataSaga() {
  yield takeLatest(FETCH_WELD_DATA, fetchWeldDataSaga);
  yield takeLatest(FETCH_WELD_PASSES, fetchWeldPassesSaga);
  yield takeLatest(FETCH_ZONE_VIEW, fetchZoneViewSaga);
  yield takeLatest(FETCH_TILT_VIEW, fetchTiltViewSaga);
  yield takeLatest(FETCH_PASS_VIEW_DETAILS, fetchPassViewDetailsSaga);
  yield takeLatest(FETCH_WELD_SEGMENT_VIEW, fetchWeldSegmentViewSaga);
  yield takeLatest(FETCH_WELD_SEGMENT_DETAIL, fetchWeldSegmentDetailSaga);
  yield takeLatest(FETCH_TILT_VIEW_DETAILS, fetchTiltViewDetailsSaga);
  yield takeLatest(FETCH_ZONE_VIEW_DETAILS, fetchZoneViewDetailsSaga);
  yield takeLatest(FETCH_PROJECTS, fetchProjectsSaga);
  yield takeLatest(FETCH_SLOGS_REQUEST, fetchSlogsSaga);
  yield takeLatest(GENERATE_REPORT_REQUEST, generateReportSaga);
  yield takeLatest(FETCH_LIMITS_DATA, fetchLimitsDataSaga);
  yield takeLatest(UPDATE_STATUS_CONFIGURATION, updateStatusConfigurationSaga);
  yield takeLatest(FETCH_MANUAL_STATUS_CONFIGURATION, fetchManualStatusConfigurationSaga);
  yield takeLatest(UPDATE_MANUAL_STATUS_CONFIGURATION, updateManualStatusConfigurationSaga);
  yield takeLatest(ASSIGN_PIPE_TO_WELD, assignPipeToWeldSaga);
  yield takeLatest(ASSIGN_JOB_NUMBER_TO_PIPE, assignJobNumberToPipeSaga);
  yield takeLatest(UPDATE_WELD_NUMBER, updateWeldNumberSaga);
  yield takeLatest(RENAME_JOB_NUMBER, renameJobNumberSaga);
  yield takeLatest(MERGE_PASSES, mergePassesSaga);
  yield takeLatest('RESTORE_MERGED_PASSES', restoreMergedPassesSaga);
} 